import { mapFromPackageTenants } from 'mappings/packageMapping';
import {
  mapFromOnboardingState,
  mapFromTenant,
  mapFromTenantLicenses,
  mapFromTenants,
  mapToOnboardingState,
  mapToTenant,
} from 'mappings/tenantMapping';
import { mapFromTimeZones } from 'mappings/timeZoneMapping';
import LicenseDTO from 'models/dto/licenseDTO';
import { PackageTenantDTO } from 'models/dto/packageDTO';
import TenantDTO, {
  BindOrgUnitDirectoryResultDTO,
  ImportOrgUnitDTO,
  TenantCRMDetailsDTO,
  TenantOnboardingDTO,
} from 'models/dto/tenantDTO';
import TimeZoneDTO from 'models/dto/timeZoneDTO';
import { IdentityProviderType } from 'models/identityProvider';
import { PackageTenant } from 'models/package';
import TimeZone from 'models/timeZone';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { toBlob } from 'utils/string';
import { SubscriptionTypes } from 'utils/subscription';
import Tenant, { TenantOnboarding } from './../../models/tenant';
import http from './httpService';

export async function apiGetTenant(accessToken: string): Promise<Tenant> {
  try {
    const ar = await http.get<TenantDTO>('/tenant', http.getRequestConfig(accessToken));

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTenant(tenant: Tenant, accessToken: string, orgUnitId?: string): Promise<Tenant> {
  try {
    const tenantDto = mapToTenant(tenant);
    const ar = await http.put<TenantDTO>(`/tenant`, tenantDto, http.getRequestConfig(accessToken, orgUnitId));

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantLicenses(tenant: Tenant, accessToken: string): Promise<Tenant> {
  try {
    const ar = await http.get<LicenseDTO>('/tenant/licenses', http.getRequestConfig(accessToken));

    return mapFromTenantLicenses(tenant, ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetOrgUnitLicenses(orgUnit: Tenant, accessToken: string): Promise<Tenant> {
  try {
    const ar = await http.get<LicenseDTO>(
      `/tenant/orgunits/${orgUnit.id}/licenses`,
      http.getRequestConfig(accessToken),
    );

    return mapFromTenantLicenses(orgUnit, ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiStartTrialPeriod(
  tenantName: string,
  onboardingState: TenantOnboarding,
  accessToken: string,
): Promise<void> {
  try {
    const onboardingStateDto = mapToOnboardingState(onboardingState);
    await http.post(`/tenant/trial?tenantName=${tenantName}`, onboardingStateDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTenantTrialOnboarding(state: TenantOnboarding, accessToken: string): Promise<void> {
  try {
    const onboardingState = mapToOnboardingState(state);

    await http.put(`/tenant/trial/onboarding`, onboardingState, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTenantOnboarding(
  state: TenantOnboarding,
  accessToken: string,
): Promise<PackageTenant[]> {
  try {
    const onboardingState = mapToOnboardingState(state);
    const ar = await http.put<PackageTenantDTO[]>(
      `/tenant/onboarding`,
      onboardingState,
      http.getRequestConfig(accessToken),
    );

    return mapFromPackageTenants(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantOnboardingState(accessToken: string): Promise<TenantOnboarding> {
  try {
    const ar = await http.get<TenantOnboardingDTO>(`/tenant/trial/onboarding`, http.getRequestConfig(accessToken));
    const state = mapFromOnboardingState(ar.data);
    if (!state) throw new AppError('No onboarding state returned');

    return state;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetOrgUnits(accessToken: string): Promise<Tenant[]> {
  try {
    const ar = await http.get<TenantDTO[]>(`/tenant/orgunits`, http.getRequestConfig(accessToken));

    return mapFromTenants(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantsForUser(accessToken: string): Promise<Tenant[]> {
  try {
    const ar = await http.get<TenantDTO[]>(`/tenant/user`, http.getRequestConfig(accessToken));

    return mapFromTenants(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddOrgUnit(
  parentId: string,
  name: string,
  isContainer: boolean,
  accessToken: string,
): Promise<Tenant> {
  try {
    const newOrgUnit = new TenantDTO();
    newOrgUnit.name = name;
    newOrgUnit.parentId = parentId;
    newOrgUnit.subscriptionType = isContainer ? SubscriptionTypes.Container : SubscriptionTypes.Premium;

    const ar = await http.post<TenantDTO>(`/tenant/orgunits`, newOrgUnit, http.getRequestConfig(accessToken));

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateOrgUnit(orgUnit: Tenant, accessToken: string): Promise<Tenant> {
  try {
    const orgUnitDto = mapToTenant(orgUnit);
    const ar = await http.put<TenantDTO>(`/tenant/orgunits`, orgUnitDto, http.getRequestConfig(accessToken));

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Result of a bind: either the org unit was linked/onboarded (tenant set), or the directory belongs to another holding's
// trial tenant and the caller must confirm onboarding it (onboardConfirmationRequired set, with name + org-unit count).
export interface OrgUnitBindResult {
  tenant?: Tenant;
  onboardConfirmationRequired: boolean;
  onboardTenantName?: string;
  onboardOrgUnitCount: number;
}

// Link an org unit to a different Entra directory. `directory` is a domain (e.g. "contoso.com",
// "contoso.onmicrosoft.com") or a tenant GUID; the backend resolves it to a tenant id via the directory's public
// OpenID Connect metadata (no token, no ISOPlanner presence required there). When the directory is another holding's
// trial tenant the acting admin belongs to, the first call returns onboardConfirmationRequired; re-call with
// confirmOnboard=true to move that whole tenant tree under this group as an org unit.
//Link an org unit to a directory of any identity provider: an Entra domain or tenant id, a Google Workspace domain.
//The API resolves the value in the provider's terms, links the org unit and provisions the admin by email.
export async function apiBindOrgUnitDirectory(
  orgUnitId: string,
  identityProviderType: IdentityProviderType,
  directory: string,
  confirmOnboard: boolean,
  accessToken: string,
): Promise<OrgUnitBindResult> {
  try {
    const ar = await http.post<BindOrgUnitDirectoryResultDTO>(
      `/tenant/orgunits/${orgUnitId}/bind`,
      { identityProviderType: identityProviderType, directory: directory, confirmOnboard: confirmOnboard },
      http.getRequestConfig(accessToken),
    );

    return {
      tenant: ar.data.tenant ? mapFromTenant(ar.data.tenant) : undefined,
      onboardConfirmationRequired: ar.data.onboardConfirmationRequired,
      onboardTenantName: ar.data.onboardTenantName,
      onboardOrgUnitCount: ar.data.onboardOrgUnitCount,
    };
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Resolve a friendly display name for an org unit's separate Entra directory (the name of the top level organization
// ISOPlanner knows for that directory). The lookup is keyed on the org unit id and authorized over it server side.
// Returns undefined when the directory is not a tenant ISOPlanner knows; the caller then keeps showing the tenant GUID.
export async function apiResolveOrgUnitDirectoryName(
  orgUnitId: string,
  accessToken: string,
): Promise<string | undefined> {
  try {
    const ar = await http.get<string>(
      `/tenant/orgunits/${orgUnitId}/directory-name`,
      http.getRequestConfig(accessToken),
    );

    return ar.data ? ar.data : undefined;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Unbind an org unit from its separate Entra directory (reset it to the top level tenant's directory). The acting admin
// is (re)provisioned into the org unit with the Admin role server side, so the caller keeps access after unbinding.
export async function apiUnbindOrgUnitDirectory(orgUnitId: string, accessToken: string): Promise<Tenant> {
  try {
    const ar = await http.post<TenantDTO>(
      `/tenant/orgunits/${orgUnitId}/unbind`,
      undefined,
      http.getRequestConfig(accessToken),
    );

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveOrgUnit(orgUnit: Tenant, accessToken: string): Promise<void> {
  try {
    const orgUnitDto = mapToTenant(orgUnit);
    await http.post<TenantDTO>(`/tenant/orgunits/delete`, orgUnitDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiImportOrgUnit(
  source: Tenant,
  target: Tenant,
  method: string,
  accessToken: string,
): Promise<void> {
  try {
    const importDto = new ImportOrgUnitDTO();
    importDto.sourceId = source.id;
    importDto.targetId = target.id;
    importDto.method = method;

    await http.post<TenantDTO>(`/tenant/orgunits/import`, importDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTenantLogo(logo: Blob | undefined, accessToken: string): Promise<void> {
  try {
    const formData = new FormData();
    if (logo) {
      formData.append('blob', logo);
    }

    await http.put(`/tenant/logo`, logo ? formData : undefined, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantLogo(accessToken: string, orgUnitId?: string): Promise<Blob | undefined> {
  try {
    const ar = await http.get(`/tenant/logo`, http.getRequestConfig(accessToken, orgUnitId));

    return toBlob(ar.data);
  } catch (err) {
    logger.debug('apiGetTenantLogo', err);

    return undefined;
  }
}

export async function apiGetTenantTimeZones(accessToken: string): Promise<TimeZone[]> {
  try {
    const ar = await http.get<TimeZoneDTO[]>(`/tenant/timezone`, http.getRequestConfig(accessToken));

    return mapFromTimeZones(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetOrgUnit(id: string, accessToken: string): Promise<Tenant | undefined> {
  try {
    const ar = await http.get<TenantDTO>(`/tenant/orgunit/${id}`, http.getRequestConfig(accessToken));

    return mapFromTenant(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTenantCRMDetails(
  id: string,
  accessToken: string,
): Promise<TenantCRMDetailsDTO | undefined> {
  try {
    const ar = await http.get<TenantCRMDetailsDTO>(`/tenant/${id}/crm/details`, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTenantCRMDetails(details: TenantCRMDetailsDTO, accessToken: string): Promise<void> {
  try {
    await http.put(`/tenant/${details.id}/crm/details`, details, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
